import { useParams } from "react-router-dom";
import { useEffect, useMemo, useState } from "react";

import { useOrganisationDetail } from "../../hooks/useOrganisation";
import { Organisation } from "../../interfaces/models/Organisation";

import { DASHBOARD_ROOT } from "../../components/Routes/routingConstants";
import { CompanySchedulesFilterStatus, CompanySchedulesFilterStatusOptions, Breadcrumb, SelectOption } from "../../helper/constants";
import Breadcrumbs from "../../components/Breadcrumbs/Breadcrumbs";
import moment from "moment";
import { useGetCompanyPolicySchedules } from "../../hooks/company-policy/useCompanyPolicySchedules";
import { Alert, Col, Input, Row } from "reactstrap";
import { CompanyPolicySchedule } from "../../interfaces/models/CompanyPolicySchedule";
import CompanyPolicySchedulesTable from "../../components/Compliance/CompanyPolicySchedulesTable";
import RichFormSelect from "../../components/Shared/Forms/RichFormSelect";

interface RouteParams {
  id: string;
}

interface FilterParams {
  search: string,
  status: SelectOption;
}

const OrganisationComplianceSchedulesPage = () => {

  const { id } = useParams<RouteParams>(); // Organisation ID

  const [breadcrumbsData, setBreadcrumbsData] = useState<Breadcrumb[]>([
    { name: "Content List", path: `${DASHBOARD_ROOT}/content-management/` },
    { name: "Organisation List", path: `${DASHBOARD_ROOT}/content-management/organisation-list` },
    { name: '...', path: `${DASHBOARD_ROOT}/content-management/organisation-list/${id}` },
    { name: 'Nudge Planner', path: `${DASHBOARD_ROOT}/content-management/organisation-list/${id}/nudge-plans` },
  ]);
  
  const today = moment().startOf('day').toDate();
  
  const organisationResult = useOrganisationDetail(id);
  const { data, error, loading, refetch } = useGetCompanyPolicySchedules({
    filter: {
      start_date: today, // Add a start date here to filter out upcoming dates
      organisation_id: id
    }
  });
  
  const [schedules, setSchedules] = useState<CompanyPolicySchedule[]>([]);

  const [filter, setFilter] = useState<FilterParams>({
    search: '',
    status: CompanySchedulesFilterStatusOptions[0],
  });
  const [reloading, setIsReloading] = useState(false);

  const handleFilterStatus = async (status: number) => {

    setIsReloading(true);
    if (status === CompanySchedulesFilterStatus.ALL) {
      await refetch({
        filter: {
          organisation_id: id
        }
      })
    }
    else if (status === CompanySchedulesFilterStatus.UPCOMING) {
      await refetch({
        filter: {
          start_date: today, // Add a start date here to filter out upcoming dates
          organisation_id: id
        }
      });
    }
    setIsReloading(false);

  }
    
  const schedulesResults = useMemo(() => {
    const search = filter.search;
    if (!search) return schedules.filter((item) => item.policy_details.name.includes(search));
    return schedules.filter((item) =>
      item.policy_details.name.toLowerCase().includes(search)
    );
  }, [filter.search, schedules]);

  useEffect(() => {
    if(data) {
      const raw = data.company_policy_schedules || [];
      setSchedules(raw);
    }
  },[data]);
  
  useEffect(() => {
    if(organisationResult.data) {
      const raw = organisationResult.data.organisation as Organisation;
      setBreadcrumbsData(prevData => {
        const updatedData = [...prevData];
        updatedData[updatedData.length - 2] = {
          ...updatedData[updatedData.length - 2], 
          name: raw.name
        };
        return updatedData;
      });
    }
  },[organisationResult.data]);
  
  return(
    <>
      <Breadcrumbs crumbs={breadcrumbsData} />
      {error && <Alert color="danger">Oops, we’re having trouble getting the schedules. Please give it a minute, and if it doesn’t resolve, let us know.</Alert>}
      <Row className="mb-1">
        <Col md={6} xs={12} className="mt-1">
            <Input 
              type="text" 
              placeholder="Search Policy names here"
              value={filter.search}
              onChange={(e) => { setFilter((prev) => ({...prev ,search: e.target.value})) }}
            />
        </Col>
        <Col md={6} xs={12} className="mt-1">
          <RichFormSelect
            value={filter.status}
            options={CompanySchedulesFilterStatusOptions}
            isSearchable={false}
            onChange={async (e) => {
              const status = e;
              setFilter((prev) => ({...prev, status: status}))
              await handleFilterStatus(Number(status.value))
            }}
          />
        </Col>
      </Row>
      <CompanyPolicySchedulesTable
        loading={loading || reloading}
        hasCRUD={false}
        schedules={schedulesResults}
      />
    </>
  )

}


export { OrganisationComplianceSchedulesPage as OrganisationComplianceSchedules };